import { TrashIcon } from '@common_components/icons/trash.icon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ModalButton from '../buttons/modalButton.component';
import { CrossIcon, LoggerService } from '@umanforge/common';

type AllowedColor = 'red' | 'blue' | 'green' | 'gray';
interface ConfirmationModalProps {
  title: string;
  description: string;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  confirmLabel?: string;
  confirmColor?: AllowedColor
  cancelLabel?: string;
  icon?: React.ReactNode;
  hasCross?: boolean;
}

const logger: LoggerService = new LoggerService( 'Confirmation Modal' );

const ConfirmationModal: React.FC<ConfirmationModalProps> = ( {
  title,
  description,
  isOpen,
  onConfirm = () => { /* */ },
  onCancel = () => { /* */ },
  onClose,
  confirmLabel,
  confirmColor = 'red',
  cancelLabel,
  icon = <TrashIcon size={7} className='text-red-base'/>,
  hasCross = true
} ) => {
  const { t } = useTranslation();

  const onModalClose = () => {
    document.body.style.overflow = 'unset';
    if ( !onClose ) {
      logger.verbose( 'no onClose function set.' );
      onCancel();
      return;
    }
    onClose();
  };

  const bgColor = new Map<AllowedColor, string>( [
    [ 'red',    'bg-red-500' ],
    [ 'blue',   'bg-blue-500' ],
    [ 'green',  'bg-green-500' ],
    [ 'gray',   'bg-gray-base' ]
  ] );
  const hoverBorderColor = new Map<AllowedColor, string>( [
    [ 'red',    'hover:border-red-500' ],
    [ 'blue',   'hover:border-blue-500' ],
    [ 'green',  'hover:border-green-500' ],
    [ 'gray',   'hover:border-gray-base' ]
  ] );

  return (
    <>
      {
        isOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center"
            onMouseDown={e => { e.stopPropagation(); }}
            onMouseUp={e => { e.stopPropagation(); }}
          >
            <div className="absolute inset-0 bg-black appear bg-opacity-40" />
            <div
              className="relative bg-white-base flex-col gap-4 rounded-xl flex shadow-xl p-6 w-fit min-w-md z-[110]"
              onMouseDown={e => { e.stopPropagation(); }}
              onMouseUp={e => { e.stopPropagation(); }}
            >
              <div className='flex'>
                <div className='flex gap-5 items-center w-full'>
                  <div className='p-2 bg-red-100 rounded-full  w-fit h-fit aspect-square'>
                    { icon }
                  </div>
                  <h2 className="text-xl font-semibold mb-2">{title}</h2>
                </div>
                <div className='right -mt-2'>
                  {
                    hasCross ?
                      <CrossIcon
                        className='w-8 h-8 cursor-pointer rounded-full justify-self-end hover:bg-gray-200 m-1 p-1'
                        onClick={ onModalClose }
                      />
                      :
                      null
                  }
                </div>
              </div>
              <div>
                <p className="text-gray-700 mb-6">{description}</p>
                <div className="flex justify-end gap-4">
                  <ModalButton
                    label={ cancelLabel ?? t( 'common:cancel' )}
                    onClick={ () => {
                      onCancel();
                      onModalClose();
                    } }
                    textColor="text-black"
                    shadow='shadow-none'
                    bgColor="bg-gray-base"
                    borderColor="border-transparent"
                    hoverBorderColor="hover:border-gray-base"
                  />

                  <ModalButton
                    label={ confirmLabel ?? t( 'common:confirm' )}
                    onClick={ () => {
                      onConfirm();
                      onModalClose();
                    } }
                    textColor="text-black"
                    shadow='shadow-none'
                    bgColor={bgColor.get( confirmColor )}
                    borderColor="border-transparent"
                    hoverBorderColor={hoverBorderColor.get( confirmColor )}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default ConfirmationModal;
